import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Input, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { useTiposServicioList } from '../api/useTiposServicio';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { Permission, type TipoServicioResponse } from '@aid/shared';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';

export function TiposServicioTable() {
  const t = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { page, pageSize, sort, handleChange, setPage } =
    useServerPaginacion<TipoServicioResponse>(20);

  const { data, isLoading, error } = useTiposServicioList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: search || undefined,
    ...sort,
  });

  const canCreate = useHasPermission(Permission.TIPOS_SERVICIO_CREATE);

  if (error) {
    return <div>Error loading tipos de servicio</div>;
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id' as keyof TipoServicioResponse,
      key: 'id',
      width: 80,
      ...serverSortProps('id', sort),
    },
    {
      title: 'Name',
      dataIndex: 'nombre' as keyof TipoServicioResponse,
      key: 'nombre',
      ...serverSortProps('nombre', sort),
      render: (val: string | null) => val ?? '-',
    },
    {
      title: 'Generates Factura',
      dataIndex: 'generaCobro' as keyof TipoServicioResponse,
      key: 'generaCobro',
      ...serverSortProps('generaCobro', sort),
      render: (val: boolean) => (val ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: TipoServicioResponse) => (
        <Tooltip title={t('common.ver')}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            aria-label={t('common.ver')}
            onClick={() => navigate(`/tipos-servicio/${record.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]}>
          <Input
            placeholder="Search tipos de servicio..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 250 }}
            allowClear
          />
        </Space>
        {canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/tipos-servicio/new')}
          >
            Create Tipo Servicio
          </Button>
        )}
      </div>
      <DataTable<TipoServicioResponse>
        columns={columns}
        dataSource={data?.data}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
        }}
        onChange={handleChange}
      />
    </div>
  );
}
